import type { DtActionsAtom } from '@/components/pages/backoffice/_klb-penyuluh/_kecamatan/state/atoms'
import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { WilayahKecamatanDataType } from './types'
import type { MutationWilayahKecamatanValues } from './validations'

const QUERY_KEY = (key?: string | Record<string, any>) =>
  ['wilker-kecamatan', key] as const

const getAll = (
  params?: DtActionsAtom
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<WilayahKecamatanDataType>>
> => ({
  queryKey: QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<WilayahKecamatanDataType>>
      >(`kelembagaan/bpp/${params?.id_bpp}/wilker-kecamatan`, {
        params: {
          page: params?.page,
          per_page: params?.perPage || undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  id: string,
  id_bpp?: string
): ServiceQueryOptionType<
  ServiceGetByIdResponse<WilayahKecamatanDataType>
> => ({
  queryKey: QUERY_KEY(id),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetByIdResponse<WilayahKecamatanDataType>
      >(`kelembagaan/bpp/${id_bpp ?? ''}/wilker-kecamatan/${id}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<WilayahKecamatanDataType>,
  MutationWilayahKecamatanValues
> => ({
  mutationKey: QUERY_KEY(undefined),
  mutationFn: async (data: MutationWilayahKecamatanValues) => {
    try {
      const response = await api.post(
        `kelembagaan/bpp/${data.id_bpp ?? ''}/wilker-kecamatan`,
        {
          ...data,
          id_kecamatan: data.id_kecamatan.value,
          desa: data.desa.map(item => item.value)
        }
      )

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<WilayahKecamatanDataType>,
  MutationWilayahKecamatanValues
> => ({
  mutationKey: QUERY_KEY(undefined),
  mutationFn: async (data: MutationWilayahKecamatanValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<WilayahKecamatanDataType>
      >(
        `kelembagaan/bpp/${data?.id_bpp ?? ''}/wilker-kecamatan/${data.id_wilayah_kecamatan}`,
        {
          ...data,
          id_kecamatan: data.id_kecamatan.value,
          desa: data.desa.map(item => item.value)
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<WilayahKecamatanDataType>,
  { id: string; id_bpp: string }
> => ({
  mutationFn: async (data: { id: string; id_bpp: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<WilayahKecamatanDataType>
      >(`kelembagaan/bpp/${data?.id_bpp ?? ''}/wilker-kecamatan/${data.id}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const wilayahKecamatan = {
  queryKey: [QUERY_KEY(undefined)[0]],
  getAll,
  getById,
  create,
  update,
  deleteData
}
